import React, { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from 'react';
import { webOrigin } from '../../utils/brand';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useDialog } from '../../context';
import { useUser } from '../../context/UserContext';
import MobileEventSheet from '../MobileEventSheet';
import ShareSheet from '../ShareSheet';
import StoriesRail from '../stories/StoriesRail';
import useMediaQuery from '../../utils/useMediaQuery';
import useTopBarHeight from '../../hooks/useTopBarHeight';
import { realPlace } from '../../utils/placeLabel';
import { RING } from '../wall/wallTokens';
import { justJoined } from '../../utils/onboardingModel';
import PeopleYouMayKnowGrid from './PeopleYouMayKnowGrid';
import { eventService, signalService } from '../../services';
import useMyPortalSources from '../../hooks/useMyPortalSources';
import ringArt from '../../assets/img/portal-ring.png';
import { openedLine } from '../../utils/portalWords';

/**
 * The City Portal — "what's happening right now" (town-square handoff,
 * docs: design_handoff_town_square/README.md). One responsive tree:
 *
 *   hero (city, live line, stat pills) → ticker → PORTALS rail → sticky tabs → tab body
 *
 * NOW · TONIGHT · TOWN · CLUBS, mirrored to the URL hash. Phones are < 640, the one-column
 * layout is < 980. Everything that used to say "signal" on this page says "portal"; the API
 * names don't change.
 */

const CYAN = '#00FFFF';
const LIVE = '#FF00AA';
const STARTER = '#FF00FF';
const LAVENDER = '#C6B8FF';
const FILL = '#F2F2F2';
const MUTED = '#6B7280';
const PASTELS = ['#FFCCE6', '#A8D8EA', '#C6B8FF', '#FAE1B4', '#E5E7EB'];
const TIMES = "'Times New Roman', Times, serif";
// Reza, Oct 27 sheet #26: TONIGHT reads as EVENTS and TOWN as NEWS. The keys (and #hash) stay.
const TABS = [['home', 'HOME'], ['tonight', 'EVENTS'], ['town', 'NEWS'], ['clubs', 'CLUBS']];
const RISE = 'prSqRise 320ms cubic-bezier(0,0,.2,1) both';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// The finished cityscape once there is one, else a Mapbox satellite still of the town centre.
function bannerImageFor(square) {
  if (square?.bannerImageUrl) return square.bannerImageUrl;
  const lat = square?.centerLat;
  const lng = square?.centerLng;
  if (MAPBOX_TOKEN && typeof lat === 'number' && typeof lng === 'number' && (lat !== 0 || lng !== 0)) {
    return 'https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/static/'
      + `${lng},${lat},10.3,0/1000x380@2x?access_token=${MAPBOX_TOKEN}&attribution=false&logo=false`;
  }
  return null;
}

const money = (cents) => `$${Math.round((cents || 0) / 100).toLocaleString()}`;
const pastelFor = (key = '') => PASTELS[[...String(key)].reduce((a, c) => a + c.charCodeAt(0), 0) % PASTELS.length];

function initialsOf(name = '') {
  const words = String(name).replace(/[^\p{L}\p{N} ]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return (words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0]).toUpperCase();
}

/** "12M", "3H", "2D" — how long ago. */
function agoLabel(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${Math.max(1, m)}M`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}H`;
  return `${Math.floor(h / 24)}D`;
}

/** "3H 41M", "41M" — how long is left. */
function leftLabel(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60);
  return h > 0 ? `${h}H ${mins % 60}M` : `${Math.max(1, mins)}M`;
}

const dayLabel = (iso) => new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '').toUpperCase();
const timeLabel = (iso) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toUpperCase();
const shortWhen = (iso) => {
  const d = new Date(iso);
  const day = d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  const h = d.getHours();
  const m = d.getMinutes();
  const hr = `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'AM' : 'PM'}`;
  return `${day} ${hr}`;
};

/* ------------------------------------------------------------------ */

const OpenEventContext = createContext(null);
function useOpenEvent() {
  const provided = useContext(OpenEventContext);
  const navigate = useNavigate();
  return provided || ((id) => id && navigate(`/events/${id}`));
}

export default function TownSquareScreen({
  square, town, feed = [], loading = false, extrasLoading = false, presence = null,
  onChangeTown, onReload, onOpenReel = null,
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const phone = useMediaQuery('(max-width: 639px)');
  const headerH = useTopBarHeight();
  const mobile = useMediaQuery('(max-width: 979px)');
  // The events sheet and the bottom tab bar both belong below lg, where the app's chrome switches.
  const sheetMode = useMediaQuery('(max-width: 1023px)');

  const [tab, setTabState] = useState(() => {
    const h = (typeof window !== 'undefined' ? window.location.hash : '').replace('#', '');
    return TABS.some(([k]) => k === h) ? h : 'home';
  });
  const setTab = useCallback((k) => {
    setTabState(k);
    // Keep React Router's own history state; only the hash changes.
    try { window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#${k}`); } catch { /* ignore */ }
  }, []);

  // A hash typed or stepped to (back / forward) picks the tab too.
  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash.replace('#', '');
      if (TABS.some(([k]) => k === h)) setTabState(h);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const tabsRef = useRef(null);
  const composerRef = useRef(null);
  /** Switch tab, then bring a section (or the tabs) into view below the sticky bars. */
  const go = useCallback((k, sectionId = null) => {
    setTab(k);
    setTimeout(() => {
      const el = sectionId && document.getElementById(sectionId);
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 150, behavior: 'smooth' });
      else if (tabsRef.current) window.scrollTo({ top: tabsRef.current.getBoundingClientRect().top + window.scrollY - headerH, behavior: 'smooth' });
    }, 60);
  }, [setTab, headerH]);

  const openComposerCard = useCallback(() => {
    go('home');
    setTimeout(() => composerRef.current?.focus({ preventScroll: true }), 420);
  }, [go]);

  const openEventId = searchParams.get('event');
  const openEvent = useCallback((id) => {
    if (!id) return;
    if (!sheetMode) { navigate(`/events/${id}`); return; }
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('event', id); return next; });
  }, [sheetMode, navigate, setSearchParams]);
  const closeEventSheet = useCallback(() => {
    const idx = window.history.state && typeof window.history.state.idx === 'number' ? window.history.state.idx : 0;
    if (idx > 0) { navigate(-1); return; }
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('event'); return next; });
  }, [navigate, setSearchParams]);

  // The rail's order, so the hero pill and the cards open the same viewer sequence it does.
  const [reels, setReels] = useState([]);
  const openStory = useCallback((userId, rect = null) => {
    if (!onOpenReel || !reels.length) return false;
    const target = userId && reels.some((r) => r.authorUserId === userId) ? userId : reels[0].authorUserId;
    onOpenReel(target, reels, rect);
    return true;
  }, [onOpenReel, reels]);

  const city = realPlace(town?.city, square?.label) || '';
  const cityUpper = (city || '…').toUpperCase();
  const location = useMemo(() => {
    const lat = typeof square?.centerLat === 'number' && square.centerLat !== 0 ? square.centerLat : town?.lat;
    const lng = typeof square?.centerLng === 'number' && square.centerLng !== 0 ? square.centerLng : town?.lng;
    return typeof lat === 'number' && typeof lng === 'number' && (lat !== 0 || lng !== 0) ? { lat, lng } : null;
  }, [square?.centerLat, square?.centerLng, town?.lat, town?.lng]);

  // A listing → the composer from that event: its poster is the picture, the words are written.
  const portalSources = useMyPortalSources();
  const portalFrom = useCallback((item) => {
    const mine = portalSources(item.id);
    if (mine?.portalId) { navigate(`/portals/${mine.portalId}`); return; }
    navigate('/portals/new', {
      state: {
        fromEvent: {
          eventId: item.id ?? null,
          title: item.name,
          startUtc: item.startsAt,
          locationLabel: item.venue || city || null,
          posterUrl: item.posterUrl || null,
          defaultText: `Going to ${item.name}${item.venue ? ` at ${item.venue}` : ''}. Who's with me?`,
        },
      },
    });
  }, [navigate, portalSources, city]);

  const sendFromSquare = useCallback((text) => {
    navigate('/portals/new', {
      state: {
        fromSquare: {
          defaultText: text || '',
          city: city ? { label: city, lat: location?.lat ?? null, lng: location?.lng ?? null } : null,
          townSquare: true,
        },
      },
    });
  }, [navigate, city, location]);

  const tonight = town?.tonight?.data || [];
  const comingSoon = town?.comingSoon?.data || [];
  const liveCount = feed.length || town?.liveCount || 0;

  const stats = [
    { key: 'live', n: liveCount, label: 'PORTALS OPEN NOW', aria: 'Watch the Portals open now', onClick: (e) => { if (!openStory(null, e.currentTarget.getBoundingClientRect())) go('home'); } },
    { key: 'tonight', n: tonight.length, label: 'EVENTS TONIGHT →', aria: 'Go to tonight’s events', onClick: () => go('tonight'), hot: true },
    { key: 'week', n: comingSoon.length, label: 'EVENTS THIS WEEK →', aria: 'Go to this week’s events', onClick: () => go('tonight', 'sec-coming') },
  ];

  const padX = phone ? 18 : 48;
  const twoCol = mobile ? 'minmax(0,1fr)' : 'minmax(0,1fr) 360px';

  return (
    <OpenEventContext.Provider value={openEvent}>
      <div className="ts-root min-h-screen bg-[#F5F5F5] font-mono text-black" style={{ paddingBottom: sheetMode ? 80 : 0 }}>
        <SquareStyles />

        <Hero
          square={square} town={town} city={cityUpper} liveCount={liveCount}
          stats={stats} phone={phone} onChangeTown={onChangeTown} padX={padX}
        />

        <StoriesRail
          variant="square"
          cityLabel={cityUpper}
          location={location}
          here={null}
          onOpenReel={onOpenReel}
          onLoaded={setReels}
          onOpenOne={openComposerCard}
          ringSize={phone ? 76 : 92}
        />

        {/* Stops under the header and never scrolls past it. */}
        <div ref={tabsRef} className="sticky z-20" style={{ top: headerH, background: '#F5F5F5', borderTop: '2px solid #000', borderBottom: '2px solid #000' }}>
          <div className="mx-auto box-border flex max-w-[1240px]" role="tablist" style={{ padding: `12px ${padX}px`, gap: 8 }}>
            {TABS.map(([k, label]) => {
              const on = tab === k;
              return (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setTab(k)}
                  className={`${on ? 'ts-tab-on ' : ''}min-w-0 whitespace-nowrap rounded-full font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2`}
                  style={{ flex: phone ? 1 : '0 0 auto', height: 44, padding: phone ? '0 4px' : '0 18px', border: on ? '3px solid transparent' : 0, background: on ? undefined : '#fff', color: on ? '#fff' : '#000', fontSize: phone ? 10 : 11, letterSpacing: '0.16em', boxSizing: 'border-box' }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <main className="mx-auto box-border max-w-[1240px]" style={{ padding: phone ? '20px 18px 40px' : '28px 48px 64px' }}>
          {loading ? <SkeletonBody twoCol={twoCol} /> : (
            <>
              {tab === 'home' && (
                <HomeTab
                  town={town} feed={feed} phone={phone} twoCol={twoCol} city={city}
                  composerRef={composerRef} onSend={sendFromSquare} onOpenStory={openStory}
                  onReload={onReload} extrasLoading={extrasLoading}
                  introducedIds={reels.filter((r) => r.introduction).map((r) => r.authorUserId)}
                />
              )}
              {tab === 'tonight' && (
                <TonightTab town={town} phone={phone} twoCol={twoCol} onOpenPortal={openComposerCard} onPortalFrom={portalFrom} portalSources={portalSources} />
              )}
              {tab === 'town' && (
                <TownTab town={town} presence={presence} phone={phone} mobile={mobile} onReload={onReload} extrasLoading={extrasLoading} />
              )}
              {tab === 'clubs' && (
                <ClubsTab town={town} onStartClub={() => navigate('/clubs/create')} navigate={navigate} />
              )}
            </>
          )}
        </main>

        <MobileEventSheet code={openEventId} open={sheetMode && !!openEventId} onClose={closeEventSheet} />
      </div>
    </OpenEventContext.Provider>
  );
}

/* ------------------------------ hero ------------------------------ */

function Hero({ square, town, city, liveCount, stats, phone, onChangeTown, padX }) {
  const { profile } = useUser();
  const src = bannerImageFor(square);
  const [imgState, setImgState] = useState('loading');
  useEffect(() => { setImgState(src ? 'loading' : 'error'); }, [src]);
  const first = (profile?.displayName || '').trim().split(' ')[0].toUpperCase();
  const eyebrow = justJoined() && first ? `YOU’RE IN, ${first} · CITY PORTAL` : 'CITY PORTAL · LOUD ON PURPOSE';
  const c = town?.conditions?.data;
  const weather = c?.tempC != null ? `${Math.round(c.tempC)}°C${c.summary ? ` · ${c.summary.toUpperCase()}` : ''}` : null;
  const pill = { height: 36, padding: '0 14px', background: 'rgba(0,0,0,.6)', fontSize: 10, fontWeight: 800, letterSpacing: '.14em' };

  return (
    <section className="relative overflow-hidden text-white" style={{ background: '#000' }}>
      {imgState === 'loading' && <div className="ts-banner-shimmer absolute inset-0" aria-hidden />}
      {src && imgState !== 'error' && (
        <img
          src={src}
          alt=""
          onLoad={() => setImgState('loaded')}
          onError={() => setImgState('error')}
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-500"
          style={{ opacity: imgState === 'loaded' ? 1 : 0, objectPosition: 'center 35%' }}
        />
      )}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,.45), rgba(0,0,0,.88))' }} />
      <div className="relative mx-auto box-border flex max-w-[1240px] flex-col" style={{ padding: phone ? `18px ${padX}px 14px` : `28px ${padX}px 22px`, gap: 10 }}>
        <span className="font-bold" style={{ fontSize: 10, letterSpacing: '0.24em', color: '#22ECFB' }}>{eyebrow}</span>
        <div className="flex flex-wrap items-end justify-between" style={{ gap: 12 }}>
          <button type="button" onClick={onChangeTown} className="flex min-w-0 items-center text-left text-white focus:outline-none focus-visible:underline" style={{ gap: 10 }} aria-label={`${city}, change town`}>
            <span className="min-w-0 break-words" style={{ fontSize: phone ? 42 : 64, fontWeight: 800, letterSpacing: '0.04em', lineHeight: 1 }}>{city}</span>
            <span style={{ fontSize: 14, color: '#BDBDBD' }} aria-hidden>▾</span>
          </button>
          {town?.localTime && <span className="font-bold" style={{ fontSize: 10, letterSpacing: '0.16em', color: '#BDBDBD', paddingBottom: 4 }}>{town.localTime.toUpperCase()}</span>}
        </div>
        <span className="flex items-center" style={{ gap: 10, fontSize: 13, lineHeight: 1.4 }}>
          <span className="pr-sq-pulse flex-shrink-0 rounded-full" style={{ width: 10, height: 10, background: LIVE }} aria-hidden />
          {liveCount === 0 ? 'Quiet right now. Open the first portal.' : `${liveCount} portal${liveCount === 1 ? '' : 's'} live right now.`}
        </span>
        <div className="ts-noscrollbar flex overflow-x-auto" style={{ gap: 8, padding: '4px 0 2px' }}>
          {stats.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={s.onClick}
              aria-label={s.aria}
              data-testid={`stat-${s.key}`}
              className="flex flex-shrink-0 items-center whitespace-nowrap rounded-full text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              style={{ ...pill, gap: 8, boxShadow: s.hot ? 'inset 0 0 0 2px #22ECFB' : 'inset 0 0 0 1px #3A3A3A' }}
            >
              <span style={{ fontSize: 14 }}>{s.n}</span> {s.label}
            </button>
          ))}
          {weather && <span className="flex flex-shrink-0 items-center whitespace-nowrap rounded-full" style={{ ...pill, boxShadow: 'inset 0 0 0 1px #3A3A3A' }}>{weather}</span>}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ HOME ------------------------------ */

const PROMPTS = ['Who’s around tonight?', 'Coffee in 20?', 'Anyone up for a walk?'];

function SectionHead({ title, meta, action = null, id }) {
  return (
    <div className="flex items-center" style={{ gap: 14 }}>
      <span id={id} className="whitespace-nowrap font-bold" style={{ fontSize: 15, letterSpacing: '0.12em', scrollMarginTop: 150 }}>{title}</span>
      {meta && <span className="whitespace-nowrap" style={{ fontSize: 11, letterSpacing: '0.1em', color: MUTED }}>{meta}</span>}
      <span className="flex-1 bg-black" style={{ height: 2 }} />
      {action}
    </div>
  );
}

function HomeTab({ town, feed, phone, twoCol, city, composerRef, onSend, onOpenStory, onReload, extrasLoading, introducedIds }) {
  const [draft, setDraft] = useState('');
  const openEvent = useOpenEvent();
  const upcoming = useMemo(() => {
    const seen = new Set();
    return [...(town?.tonight?.data || []), ...(town?.comingSoon?.data || [])]
      .filter((e) => { if (seen.has(e.id)) return false; seen.add(e.id); return new Date(e.startsAt).getTime() >= Date.now() - 3 * 3600 * 1000; })
      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
  }, [town?.tonight?.data, town?.comingSoon?.data]);
  const send = () => onSend(draft.trim());
  const shown = upcoming.slice(0, phone ? 2 : 3);

  return (
    <div className="grid items-start" style={{ gridTemplateColumns: twoCol, gap: phone ? 32 : 40 }}>
      <div className="flex min-w-0 flex-col" style={{ gap: 32 }}>
        {/* OPEN A PORTAL */}
        <div className="ts-ring-card" style={{ padding: 3, animation: RISE }}>
          <div className="flex flex-col bg-black text-white" style={{ padding: 20, gap: 14 }}>
            <div className="flex flex-wrap items-baseline justify-between" style={{ gap: 10 }}>
              <span className="font-bold" style={{ fontSize: 13, letterSpacing: '0.22em', color: '#22ECFB' }}>OPEN A PORTAL</span>
              <span className="font-bold" style={{ fontSize: 9, letterSpacing: '0.14em', color: '#8A8A8A' }}>LIVE 4 HRS · YOU PICK WHO&apos;S IN</span>
            </div>
            <div className="flex" style={{ gap: 8 }}>
              <input
                ref={composerRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                placeholder="What do you want to do?"
                aria-label="What do you want to do?"
                autoComplete="off"
                className="ts-dark-input min-w-0 flex-1 rounded-full border-0 font-mono text-white focus:outline-none focus:ring-0"
                style={{ height: 50, padding: '0 20px', background: '#1F1F1F', fontSize: 14 }}
                maxLength={140}
              />
              <button
                type="button"
                onClick={send}
                className="flex-shrink-0 rounded-full bg-white font-bold text-black transition-colors hover:bg-[#FF00AA] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                style={{ height: 50, padding: '0 22px', fontSize: 11, letterSpacing: '0.16em' }}
              >
                SEND →
              </button>
            </div>
            <div className="ts-noscrollbar flex overflow-x-auto" style={{ gap: 8 }}>
              {PROMPTS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => { setDraft(p); composerRef.current?.focus({ preventScroll: true }); }}
                  className="flex-shrink-0 whitespace-nowrap rounded-full text-white transition-colors hover:bg-[#2A2A2A]"
                  style={{ height: 36, padding: '0 16px', background: '#1F1F1F', fontFamily: TIMES, fontStyle: 'italic', fontSize: 15 }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        <PeopleYouMayKnowGrid city={city} phone={phone} introducedIds={introducedIds} onOpenStory={onOpenStory} />

        {shown.length > 0 && (
          <section className="flex flex-col" style={{ gap: 14 }}>
            <div className="flex items-center" style={{ gap: 14 }}>
              <span className="whitespace-nowrap font-bold" style={{ fontSize: 14, letterSpacing: '0.14em' }}>FEATURED EVENTS</span>
              <span className="flex-1" style={{ height: 2, background: '#000' }} />
              <Link to="/featured" className="whitespace-nowrap font-bold text-black hover:text-[#FF00AA]" style={{ fontSize: 11, letterSpacing: '0.14em' }}>ALL {upcoming.length} →</Link>
            </div>
            <div className="grid" style={{ gridTemplateColumns: `repeat(${phone ? 2 : 3},minmax(0,1fr))`, gap: 12 }}>
              {shown.map((e) => (
                <button key={e.id} type="button" onClick={() => openEvent(e.id)} data-testid="featured-event" className="flex min-w-0 flex-col bg-white text-left text-black hover:opacity-90">
                  <span className="relative block w-full overflow-hidden" style={{ aspectRatio: '16/10', background: '#111' }}>
                    {e.posterUrl && <img src={e.posterUrl} alt="" loading="lazy" className="block h-full w-full object-cover" />}
                    <span className="absolute flex items-center rounded-full bg-black font-bold text-white" style={{ left: 10, top: 10, height: 22, padding: '0 9px', fontSize: 9, letterSpacing: '0.12em' }}>{shortWhen(e.startsAt)}</span>
                  </span>
                  <span className="flex flex-col" style={{ padding: '12px 12px 14px', gap: 6 }}>
                    <span className="ts-clamp2 font-bold" style={{ fontSize: 13, lineHeight: 1.3 }}>{e.name}</span>
                    {e.venue && <span className="truncate font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.1em', color: MUTED }}>{e.venue}</span>}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>

      <aside className="flex min-w-0 flex-col" style={{ gap: 20 }}>
        <section className="flex flex-col" style={{ gap: 12 }}>
          <SectionHead title="RIGHT NOW" meta={`${feed.length} PORTAL${feed.length === 1 ? '' : 'S'} OPEN`} />
          {feed.length === 0 ? (
            <div className="bg-white" style={{ padding: 20, animation: RISE }}>
              <p style={{ fontFamily: TIMES, fontStyle: 'italic', fontSize: 24, lineHeight: 1.2 }}>Nobody&apos;s opened one yet.</p>
              <p className="mt-2" style={{ fontSize: 12, lineHeight: 1.6, color: '#4B5563' }}>Open the first portal above. It&apos;s live for four hours and you pick who&apos;s in.</p>
            </div>
          ) : feed.map((s) => (
            <PortalCard key={s.eventId} signal={s} phone onOpenStory={onOpenStory} />
          ))}
        </section>
        <BoardPanel town={town} slug={town?.slug} onReload={onReload} extrasLoading={extrasLoading} />
        <ConditionsMini town={town} />
        <p style={{ fontSize: 11, lineHeight: 1.7, letterSpacing: '.04em', color: '#4B5563' }}>
          <b className="text-black" style={{ letterSpacing: '0.16em' }}>CITY RULES ·</b> Everyone near you is in here. Be kind. Be yourself. Hosts pick who&apos;s in. The address shows when you&apos;re in.
        </p>
      </aside>
    </div>
  );
}

/** A spinning ring with a round face in it — the Portal mark at any size. */
function RingFace({ size, url, initial, tint = '#1A1A1A', spin = 'ts-spin-6', fontSize = 15 }) {
  return (
    <span className="relative block flex-shrink-0" style={{ width: size, height: size }} aria-hidden>
      <span className={`${spin} absolute inset-0 rounded-full`} style={{ backgroundImage: `url(${ringArt})`, backgroundSize: 'cover' }} />
      <span className="absolute flex items-center justify-center overflow-hidden rounded-full font-bold text-white" style={{ inset: '5.5%', background: tint, fontSize }}>
        {url ? <img src={url} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : initial}
      </span>
    </span>
  );
}

function PortalCard({ signal, phone, onOpenStory }) {
  const navigate = useNavigate();
  const openEvent = useOpenEvent();
  const [state, setState] = useState(signal.viewerState || 'open');
  const [count, setCount] = useState(signal.acceptedCount || 0);
  const [busy, setBusy] = useState(false);
  const [shareFrom, setShareFrom] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const padX = phone ? 16 : 22;

  const isMine = signal.isMine || state === 'host';
  const joined = state === 'accepted';
  const pending = state === 'applied_pending';
  const faces = (signal.invitees || []).filter((p) => p.state === 'accepted').slice(0, 4);
  const place = signal.venueName || signal.locationLabel || signal.primaryCityLabel;
  const ago = agoLabel(signal.dateTimeUtc);
  const left = leftLabel(signal.expiresAtUtc);
  const src = signal.sourceEvent;

  const toggleIn = async () => {
    if (busy || isMine || pending) return;
    setBusy(true);
    if (joined) {
      const r = await signalService.withdraw(signal.eventId);
      if (r?.success) { setState('open'); setCount((n) => Math.max(0, n - 1)); }
    } else {
      const r = await signalService.imDown(signal.eventId);
      if (r?.success) {
        const next = r.data?.viewerState || 'accepted';
        setState(next);
        if (next === 'accepted') setCount((n) => n + 1);
        // Into the Portal: its page has the guest list and where it is.
        navigate(`/portals/${signal.eventId}?joined=1`);
      }
    }
    setBusy(false);
  };

  const openPortal = (e) => {
    if (!onOpenStory(signal.hostUserId, e.currentTarget.getBoundingClientRect())) navigate(`/portals/${signal.eventId}`);
  };

  const inBtn = isMine
    ? { label: 'MANAGE', bg: '#000', fg: '#fff', onClick: () => navigate(`/portals/${signal.eventId}`) }
    : pending
      ? { label: 'ASKED', bg: FILL, fg: '#000', onClick: undefined }
      : joined
        ? { label: 'YOU’RE IN', bg: CYAN, fg: '#000', onClick: toggleIn }
        : { label: 'I’M IN', bg: '#000', fg: '#fff', onClick: toggleIn };

  return (
    <article className="flex flex-col bg-white" style={{ animation: RISE }} data-testid="portal-card">
      <div className="flex items-center" style={{ gap: 12, padding: `16px ${padX}px 0` }}>
        {signal.hostHandle ? (
          <Link to={`/${signal.hostHandle}`} className="block flex-shrink-0 hover:opacity-80" aria-label={signal.hostDisplayName}>
            <RingFace size={46} url={signal.hostAvatarUrl} initial={(signal.hostDisplayName || '?').charAt(0).toUpperCase()} />
          </Link>
        ) : <RingFace size={46} url={signal.hostAvatarUrl} initial={(signal.hostDisplayName || '?').charAt(0).toUpperCase()} />}
        <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
          <span className="truncate" style={{ fontSize: 14 }}><b>{signal.hostDisplayName}</b> opened a portal</span>
          <span className="truncate uppercase" style={{ fontSize: 10, letterSpacing: '0.12em', color: MUTED }}>
            {[ago ? `${ago} AGO` : null, place].filter(Boolean).join(' · ')}
          </span>
        </div>
        {left && <span className="flex-shrink-0 whitespace-nowrap font-bold text-white" style={{ background: LIVE, fontSize: 9, letterSpacing: '0.12em', padding: '4px 8px' }}>{left} LEFT</span>}
      </div>

      <button type="button" onClick={openPortal} className="m-0 block w-full text-left text-black hover:opacity-80" style={{ padding: `16px ${padX}px 0` }}>
        <span style={{ fontFamily: TIMES, fontStyle: 'italic', fontSize: phone ? 22 : 26, lineHeight: 1.2, textWrap: 'pretty' }}>{signal.signalText ? `“${signal.signalText}”` : openedLine(signal.hostDisplayName)}</span>
      </button>

      {src && (
        <button
          type="button"
          onClick={() => openEvent(src.eventId)}
          className="flex items-center text-left text-black hover:opacity-80"
          style={{ margin: `16px ${padX}px 0`, gap: 12, background: FILL }}
        >
          <span className="block flex-shrink-0" style={{ width: 64, height: 64, background: '#111' }}>
            {src.posterUrl && <img src={src.posterUrl} alt="" className="block h-full w-full object-cover" />}
          </span>
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
            <span className="font-bold" style={{ fontSize: 9, letterSpacing: '0.18em', color: MUTED }}>THE EVENT</span>
            <span className="truncate font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.06em' }}>{src.title}</span>
          </span>
          <span style={{ paddingRight: 14, fontSize: 16 }} aria-hidden>→</span>
        </button>
      )}

      <div className="flex flex-wrap items-center justify-between" style={{ gap: 12, padding: `16px ${padX}px 18px` }}>
        <span className="flex min-w-0 items-center" style={{ gap: 10, flex: '1 1 auto' }}>
          {faces.length > 0 && (
            <span className="flex flex-shrink-0" style={{ paddingLeft: 10 }}>
              {faces.map((f) => (
                <span key={f.userId} className="box-border flex items-center justify-center overflow-hidden rounded-full font-bold" style={{ width: 30, height: 30, marginLeft: -10, border: '2px solid #fff', background: pastelFor(f.displayName), fontSize: 10 }}>
                  {f.avatarUrl ? <img src={f.avatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : initialsOf(f.displayName)}
                </span>
              ))}
            </span>
          )}
          <span className="whitespace-nowrap font-bold" style={{ fontSize: 10, letterSpacing: '0.12em' }}>{count} IN</span>
          <span className="whitespace-nowrap rounded-full font-bold" style={{ padding: '3px 8px', background: LAVENDER, fontSize: 9, letterSpacing: '0.12em' }}>
            {signal.clubName ? 'CLUB' : 'CITY PORTAL'}
          </span>
        </span>
        <span className="ml-auto flex flex-shrink-0 items-center" style={{ gap: 8 }}>
          <button
            type="button"
            onClick={inBtn.onClick}
            disabled={busy || !inBtn.onClick}
            aria-pressed={joined}
            className="rounded-full font-bold transition-colors disabled:cursor-default"
            style={{ height: 42, padding: '0 20px', background: inBtn.bg, color: inBtn.fg, fontSize: 12, letterSpacing: '0.16em', opacity: busy ? 0.6 : 1 }}
          >
            {inBtn.label}
          </button>
          <button
            type="button"
            aria-label="Share this portal"
            onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setShareFrom({ left: r.left, top: r.top, width: r.width, height: r.height }); setShareOpen(true); }}
            className="relative flex-shrink-0 rounded-full hover:opacity-80"
            style={{ width: 42, height: 42 }}
          >
            <span className="absolute inset-0 rounded-full" style={{ backgroundImage: `url(${ringArt})`, backgroundSize: 'cover' }} aria-hidden />
            <span className="absolute flex items-center justify-center rounded-full bg-white" style={{ inset: 3 }} aria-hidden>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.25" strokeLinecap="square"><path d="M12 3v12M7 8l5-5 5 5M5 13v8h14v-8" /></svg>
            </span>
          </button>
        </span>
      </div>

      <ShareSheet
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        originRect={shareFrom}
        title={signal.signalText || openedLine(signal.hostDisplayName)}
        url={`${webOrigin()}/portals/${signal.eventId}`}
        inviteCode={signal.inviteCode}
        date={signal.dateTimeUtc}
        mode={signal.mode}
        eventId={signal.eventId}
        eventService={eventService}
      />
    </article>
  );
}

function InTheSquare({ town, presence }) {
  const here = presence
    ? { count: presence.count, people: presence.people || (presence.initials || []).map((i) => ({ initials: i })) }
    : town?.liveHere?.data;
  const people = here?.people || [];
  return (
    <div className="bg-white" style={{ padding: 18 }}>
      <div className="flex items-baseline justify-between" style={{ marginBottom: 12 }}>
        <span className="font-bold" style={{ fontSize: 11, letterSpacing: '0.16em' }}>IN TOWN · {here?.count ?? 0}</span>
        <span style={{ fontSize: 9, letterSpacing: '0.12em', color: MUTED }}>NEAR YOU NOW</span>
      </div>
      {people.length > 0 ? (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(34px,1fr))', gap: 6 }}>
          {people.map((p, i) => {
            const tile = (
              <span className="flex items-center justify-center overflow-hidden font-bold" style={{ aspectRatio: '1', background: '#E5E7EB', fontSize: 9 }}>
                {p.avatarUrl ? <img src={p.avatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : (p.initials || '?')}
              </span>
            );
            return p.handle
              ? <Link key={i} to={`/${p.handle}`} title={p.name || p.initials} className="block hover:opacity-70">{tile}</Link>
              : <React.Fragment key={i}>{tile}</React.Fragment>;
          })}
        </div>
      ) : null}
      <p style={{ margin: people.length ? '12px 0 0' : 0, fontSize: 11, color: MUTED }}>
        {people.length ? 'Say hi to someone. Nobody here bites.' : 'Nobody’s here right now.'}
      </p>
    </div>
  );
}

function ConditionsMini({ town }) {
  const c = town?.conditions?.data;
  const alert = town?.alert?.data;
  if (!c) return null;
  const cells = [
    { v: c.tempC != null ? `${Math.round(c.tempC)}°C` : '—', l: (c.summary || 'WEATHER').toUpperCase() },
    { v: c.sunsetAt || '—', l: 'SUNSET' },
    { v: c.lakeTempC != null ? `${c.lakeTempC.toFixed(1)}°` : '—', l: (c.lakeName || 'WATER').toUpperCase() },
    { v: c.aqi != null ? `AQI ${c.aqi}` : '—', l: (c.aqiLabel || 'AIR').toUpperCase() },
  ];
  return (
    <div className="grid bg-black text-white" style={{ padding: 18, gridTemplateColumns: '1fr 1fr', gap: 14 }}>
      {cells.map((x) => (
        <span key={x.l} className="flex min-w-0 flex-col" style={{ gap: 2 }}>
          <span className="font-bold" style={{ fontSize: 20 }}>{x.v}</span>
          <span className="truncate" style={{ fontSize: 9, letterSpacing: '0.14em', color: 'rgba(255,255,255,.6)' }}>{x.l}</span>
        </span>
      ))}
      {alert?.text && (
        <span className="flex items-center font-bold uppercase" style={{ gridColumn: '1 / -1', gap: 8, fontSize: 10, letterSpacing: '0.12em' }}>
          <span className="flex-shrink-0" style={{ width: 8, height: 8, background: LIVE }} />{alert.text}
        </span>
      )}
    </div>
  );
}

/* ------------------------------ TONIGHT ------------------------------ */

function BeaconButton({ item, portal, onClick }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(item); }}
      aria-label={portal ? `Your portal for ${item.name}` : `Open a portal to ${item.name}`}
      title={portal ? 'PORTAL OPEN' : 'OPEN A PORTAL'}
      className="relative flex-shrink-0 rounded-full hover:opacity-85 focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
      style={{ width: 40, height: 40 }}
    >
      <span className="ts-spin-6 absolute inset-0 rounded-full" style={{ backgroundImage: `url(${ringArt})`, backgroundSize: 'cover' }} aria-hidden />
      <span className="absolute flex items-center justify-center rounded-full" style={{ inset: 3, background: portal ? CYAN : '#000', color: portal ? '#000' : '#fff' }} aria-hidden>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="7" r="2" /><path d="M12 9V21" /><path d="M8.4 3.6a5.5 5.5 0 0 0 0 6.8" /><path d="M15.6 3.6a5.5 5.5 0 0 1 0 6.8" /></svg>
      </span>
    </button>
  );
}

function TonightTab({ town, phone, twoCol, onOpenPortal, onPortalFrom, portalSources }) {
  const openEvent = useOpenEvent();
  const tonight = useMemo(() => town?.tonight?.data || [], [town?.tonight?.data]);
  const days = useMemo(() => {
    const seen = new Set();
    const all = [...tonight, ...(town?.comingSoon?.data || [])]
      .filter((e) => { if (seen.has(e.id)) return false; seen.add(e.id); return true; })
      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
    const map = new Map();
    all.forEach((e) => { const k = dayLabel(e.startsAt); if (!map.has(k)) map.set(k, []); map.get(k).push(e); });
    return [...map.entries()];
  }, [tonight, town?.comingSoon?.data]);
  const thumb = phone ? 64 : 76;

  return (
    <div className="grid items-start" style={{ gridTemplateColumns: twoCol, gap: 24 }}>
      <div className="flex min-w-0 flex-col" style={{ gap: 22 }}>
        <div className="flex flex-wrap items-center justify-between bg-black text-white" style={{ padding: phone ? '18px 16px' : '22px 24px', gap: 16, animation: RISE }}>
          <span className="flex flex-col" style={{ gap: 6 }}>
            <span className="font-bold" style={{ fontSize: 11, letterSpacing: '0.2em', color: CYAN }}>ON TONIGHT · {tonight.length}</span>
            <span style={{ fontSize: 15 }}>
              {tonight.length === 0
                ? 'Nothing on the books tonight. Open a portal and make it something.'
                : `${tonight.length} thing${tonight.length === 1 ? '' : 's'} on tonight. Open a portal and bring people.`}
            </span>
          </span>
          <button type="button" onClick={onOpenPortal} className="rounded-full bg-white font-bold text-black transition-colors hover:bg-[#00FFFF]" style={{ height: 44, padding: '0 22px', fontSize: 12, letterSpacing: '0.14em' }}>
            OPEN A PORTAL
          </button>
        </div>

        <span id="sec-coming" className="block" style={{ height: 0, scrollMarginTop: 140 }} />
        {days.length === 0 && (
          <p className="bg-white uppercase" style={{ padding: 18, fontSize: 11, letterSpacing: '0.1em', color: MUTED }}>Nothing listed for the week yet.</p>
        )}
        {days.map(([day, items]) => (
          <div key={day} className="flex flex-col" style={{ gap: 10 }}>
            <div className="flex items-center" style={{ gap: 12 }}>
              <span className="rounded-full bg-black font-bold text-white" style={{ padding: '5px 12px', fontSize: 10, letterSpacing: '0.16em' }}>{day}</span>
              <span style={{ fontSize: 10, letterSpacing: '0.12em', color: MUTED }}>{items.length} EVENT{items.length === 1 ? '' : 'S'}</span>
              <span className="flex-1 bg-black" style={{ height: 2 }} />
            </div>
            {items.map((e) => (
              <div
                key={e.id}
                role="button"
                tabIndex={0}
                onClick={() => openEvent(e.id)}
                onKeyDown={(ev) => { if (ev.key === 'Enter') openEvent(e.id); }}
                className="flex cursor-pointer items-center bg-white text-black hover:opacity-90"
                style={{ gap: 14, padding: 10, animation: RISE }}
                data-testid="tonight-row"
              >
                <span className="block flex-shrink-0" style={{ width: thumb, height: thumb, background: '#111' }}>
                  {e.posterUrl && <img src={e.posterUrl} alt="" loading="lazy" className="block h-full w-full object-cover" />}
                </span>
                <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 4 }}>
                  <span className="font-bold" style={{ fontSize: 10, letterSpacing: '0.14em' }}>{timeLabel(e.startsAt)}</span>
                  <span className="ts-clamp2" style={{ fontSize: 14, lineHeight: 1.25 }}>{e.name}</span>
                  {e.venue && <span className="truncate uppercase" style={{ fontSize: 9, letterSpacing: '0.12em', color: MUTED }}>{e.venue}</span>}
                </span>
                <BeaconButton item={e} portal={portalSources(e.id)} onClick={onPortalFrom} />
              </div>
            ))}
          </div>
        ))}
      </div>

      <aside className="flex min-w-0 flex-col" style={{ gap: 16 }}>
        <TicketsPanel town={town} />
        <GreenlitPanel town={town} />
      </aside>
    </div>
  );
}

function TicketsPanel({ town }) {
  const openEvent = useOpenEvent();
  const tickets = town?.tickets?.data || [];
  return (
    <div className="flex flex-col bg-white" style={{ padding: 18, gap: 12 }}>
      <span className="font-bold" style={{ fontSize: 11, letterSpacing: '0.16em' }}>TICKETS · ON SALE</span>
      {tickets.length === 0 && <NoData label="Nothing on sale right now." />}
      {tickets.map((t) => (
        <button key={t.id} type="button" onClick={() => openEvent(t.id)} className="flex items-center text-left text-black hover:opacity-80" style={{ gap: 12 }}>
          <span className="block flex-shrink-0" style={{ width: 48, height: 48, background: '#111' }}>
            {t.posterUrl && <img src={t.posterUrl} alt="" className="block h-full w-full object-cover" />}
          </span>
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
            <span className="truncate" style={{ fontSize: 13 }}>{t.name}</span>
            <span style={{ fontSize: 9, letterSpacing: '0.12em', color: MUTED }}>
              {new Date(t.startsAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()} · {t.capacity ? `${t.sold}/${t.capacity}` : `${t.sold} IN`}
            </span>
          </span>
          <span className="flex-shrink-0 font-bold" style={{ fontSize: 14 }}>{t.priceCents ? money(t.priceCents) : 'FREE'}</span>
        </button>
      ))}
    </div>
  );
}

function GreenlitPanel({ town }) {
  const openEvent = useOpenEvent();
  const rows = town?.greenlit?.data || [];
  return (
    <div className="flex flex-col bg-white" style={{ padding: 18, gap: 14 }}>
      <span className="font-bold" style={{ fontSize: 11, letterSpacing: '0.16em' }}>GREENLIT THIS WEEK</span>
      {rows.length === 0 && <NoData label="Nothing greenlit this week." />}
      {rows.map((g) => {
        const s = (g.status || '').toLowerCase();
        const funding = s === 'funding' || s === 'processing_greenlight';
        return (
          <button key={g.id} type="button" onClick={() => openEvent(g.id)} className="flex flex-col text-left text-black hover:opacity-80" style={{ gap: 10 }}>
            <span className="flex w-full justify-between" style={{ gap: 10, fontSize: 13 }}>
              <span className="min-w-0">{g.name}</span>
              <span className="flex-shrink-0 self-center rounded-full font-bold text-white" style={{ padding: '2px 8px', background: funding ? STARTER : '#000', fontSize: 9, letterSpacing: '0.12em' }}>
                {funding ? 'FUNDING' : 'GREENLIT'}
              </span>
            </span>
            <span className="block w-full" style={{ height: 4, background: '#FBD5FB' }}>
              <span className="block" style={{ height: 4, width: `${Math.min(100, g.percent || 0)}%`, background: STARTER }} />
            </span>
            <span style={{ fontSize: 9, letterSpacing: '0.12em', color: MUTED }}>{Math.min(100, g.percent || 0)}% · {money(g.raisedCents)}{g.goalCents ? ` OF ${money(g.goalCents)}` : ''}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------ TOWN ------------------------------ */

const boxPending = (extrasLoading, field) => extrasLoading && field === undefined;

function TownTab({ town, presence, phone, mobile, onReload, extrasLoading }) {
  const c = town?.conditions?.data;
  const alert = town?.alert?.data;
  const tiles = c ? [
    { v: c.tempC != null ? `${Math.round(c.tempC)}°C` : '—', l: `${(c.summary || 'WEATHER').toUpperCase()}${c.feelsLikeC != null ? ` · FEELS ${Math.round(c.feelsLikeC)}°` : ''}` },
    { v: c.lakeTempC != null ? `${c.lakeTempC.toFixed(1)}°` : '—', l: (c.lakeName || 'WATER').toUpperCase() },
    { v: c.sunsetAt || '—', l: 'SUNSET' },
    { v: c.aqi != null ? `AQI ${c.aqi}` : '—', l: c.aqiLabel ? `AIR · ${c.aqiLabel.toUpperCase()}` : 'AIR' },
  ] : null;

  return (
    <div className="flex flex-col" style={{ gap: 20 }}>
      <InTheSquare town={town} presence={presence} />
      {tiles ? (
        <div className="grid bg-black" style={{ gridTemplateColumns: phone ? 'repeat(2,minmax(0,1fr))' : 'repeat(4,minmax(0,1fr))', gap: 2, border: '2px solid #000', animation: RISE }}>
          {tiles.map((t) => (
            <div key={t.l} className="flex min-w-0 flex-col bg-white" style={{ padding: 18, gap: 4 }}>
              <span className="font-bold" style={{ fontSize: phone ? 24 : 34 }}>{t.v}</span>
              <span style={{ fontSize: 10, letterSpacing: '0.14em', color: MUTED }}>{t.l}</span>
            </div>
          ))}
          {alert?.text && (
            <div className="flex items-center bg-black font-bold uppercase text-white" style={{ gridColumn: '1 / -1', padding: '12px 18px', gap: 10, fontSize: 12, letterSpacing: '0.12em' }}>
              <span className="flex-shrink-0" style={{ width: 10, height: 10, background: LIVE }} />{alert.text}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white" style={{ padding: 18 }}><NoData label="No conditions for this town right now." /></div>
      )}

      <div className="grid items-start" style={{ gridTemplateColumns: mobile ? 'minmax(0,1fr)' : 'minmax(0,1fr) 380px', gap: 20 }}>
        <div className="flex min-w-0 flex-col" style={{ gap: 20 }}>
          <HeadlinesPanel town={town} phone={phone} extrasLoading={extrasLoading} />
          <NewInTownPanel town={town} extrasLoading={extrasLoading} />
        </div>
        <div className="flex min-w-0 flex-col" style={{ gap: 20 }}>
          <OpenNowPanel town={town} extrasLoading={extrasLoading} />
          <BoardPanel town={town} slug={town?.slug} onReload={onReload} extrasLoading={extrasLoading} />
          <TheLine town={town} extrasLoading={extrasLoading} />
          <LocalsPanel town={town} extrasLoading={extrasLoading} />
          <DealsPanel town={town} extrasLoading={extrasLoading} />
        </div>
      </div>
    </div>
  );
}

function PanelHead({ title, src, id }) {
  return (
    <div className="flex justify-between" style={{ gap: 10 }}>
      <span id={id} className="font-bold" style={{ fontSize: 11, letterSpacing: '0.16em', scrollMarginTop: 150 }}>{title}</span>
      {src && <span style={{ fontSize: 9, letterSpacing: '0.12em', color: '#9CA3AF' }}>{src}</span>}
    </div>
  );
}

// Google News titles end with " - Publisher"; the publisher already has its own line.
export function cleanHeadline(title = '', source = '') {
  const i = title.lastIndexOf(' - ');
  if (i <= 0) return title;
  const tail = title.slice(i + 3).trim().toLowerCase();
  const src = (source || '').trim().toLowerCase();
  return !src || tail === src || src.includes(tail) || tail.includes(src) ? title.slice(0, i).trim() : title;
}

function HeadlinesPanel({ town, phone, extrasLoading }) {
  if (boxPending(extrasLoading, town?.headlines)) return <SkeletonPanel rows={4} />;
  const rows = (town?.headlines?.data || []).map((h) => ({ ...h, title: cleanHeadline(h.title, h.source) }));
  const leadIdx = rows.findIndex((h) => h.imageUrl);
  const lead = leadIdx >= 0 ? rows[leadIdx] : null;
  const rest = rows.filter((_, i) => i !== leadIdx);
  const stamp = (h) => [h.source, agoLabel(h.publishedAt)].filter(Boolean).join(' · ').toUpperCase();

  return (
    <div className="flex flex-col bg-white" style={{ padding: 18, gap: 10 }}>
      <PanelHead title="LOCAL HEADLINES" src="SRC: RSS" />
      {rows.length === 0 && <NoData label="No local headlines right now." />}
      {lead && (
        <a href={lead.url || undefined} target="_blank" rel="noreferrer" className="relative block overflow-hidden text-white hover:opacity-95" style={{ aspectRatio: phone ? '4 / 3' : '16 / 9', background: '#111' }} data-testid="headline-lead">
          <HeadlineImg src={lead.imageUrl} className="absolute inset-0 h-full w-full object-cover" />
          <span className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,.85), rgba(0,0,0,0) 60%)' }} />
          <span className="absolute flex flex-col" style={{ left: 16, right: 16, bottom: 14, gap: 6 }}>
            {stamp(lead) && <span className="self-start rounded-full bg-white font-bold text-black" style={{ padding: '3px 8px', fontSize: 9, letterSpacing: '0.14em' }}>{stamp(lead)}</span>}
            <span style={{ fontSize: phone ? 16 : 19, lineHeight: 1.25, fontWeight: 600, textWrap: 'pretty' }}>{lead.title}</span>
          </span>
        </a>
      )}
      {rest.map((h, i) => (
        <a key={i} href={h.url || undefined} target="_blank" rel="noreferrer" className="flex items-center text-black hover:opacity-80" style={{ gap: 12, padding: '10px 0', borderTop: lead || i > 0 ? '1px solid #EEE' : 'none' }}>
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 4 }}>
            <span className="ts-clamp3" style={{ fontSize: 13, lineHeight: 1.4 }}>{h.title}</span>
            {stamp(h) && <span style={{ fontSize: 9, letterSpacing: '0.12em', color: MUTED }}>{stamp(h)}</span>}
          </span>
          <HeadlineThumb h={h} />
        </a>
      ))}
    </div>
  );
}

function HeadlineImg({ src, className }) {
  const [ok, setOk] = useState(true);
  if (!ok) return null;
  return <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setOk(false)} className={className} />;
}

function HeadlineThumb({ h }) {
  const [ok, setOk] = useState(!!h.imageUrl);
  if (ok) {
    return <img src={h.imageUrl} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setOk(false)} className="block flex-shrink-0 object-cover" style={{ width: 76, height: 76 }} />;
  }
  return (
    <span className="flex flex-shrink-0 flex-col items-center justify-center" style={{ width: 76, height: 76, gap: 2, background: pastelFor(h.source || h.title) }} aria-hidden>
      <span style={{ fontSize: 18, fontWeight: 800 }}>{initialsOf(h.source || h.title)}</span>
      <span className="font-bold" style={{ fontSize: 7, letterSpacing: '0.12em' }}>NO IMAGE</span>
    </span>
  );
}

function viaLabel(n) {
  const who = n.sourceName ? ` · ${n.sourceName.toUpperCase()}` : '';
  if (n.source === 'portal') return `VIA A PORTAL${who}`;
  if (n.source === 'club_invite') return `VIA CLUB INVITE${who}`;
  if (n.source === 'event') return 'VIA EVENT';
  return 'JUST JOINED';
}

function NewInTownPanel({ town, extrasLoading }) {
  if (boxPending(extrasLoading, town?.newcomers)) return <SkeletonPanel rows={3} />;
  const rows = [...(town?.newcomers?.data || [])].sort((a, b) => (a.daysIn ?? 0) - (b.daysIn ?? 0));
  return (
    <div className="flex flex-col bg-white" style={{ padding: 18, gap: 10 }}>
      <PanelHead title="NEW IN TOWN" src="SRC: PORTAL" id="sec-new" />
      {rows.length === 0 ? <NoData label="No new members this month." /> : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%,220px),1fr))', gap: 8 }}>
          {rows.map((n, i) => {
            const newest = i === 0;
            const body = (
              <span className="relative flex items-center overflow-hidden" style={{ gap: 10, padding: 8, background: newest ? '#E6FFFF' : FILL }} data-testid={newest ? 'newcomer-newest' : 'newcomer'}>
                {newest && <span aria-hidden className="ts-row-shine pointer-events-none absolute inset-0" />}
                <span className="relative flex-shrink-0" style={{ width: 34, height: 34 }}>
                  {newest && <span className="ts-spin-ccw-5 absolute inset-0 rounded-full" style={{ backgroundImage: `url(${ringArt})`, backgroundSize: 'cover' }} aria-hidden />}
                  <span className="absolute flex items-center justify-center overflow-hidden rounded-full bg-white font-bold" style={{ inset: newest ? '5.5%' : 0, fontSize: 9 }}>
                    {n.avatarUrl ? <img src={n.avatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : n.initials}
                  </span>
                </span>
                <span className="relative flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
                  <span className="truncate" style={{ fontSize: 12, fontWeight: newest ? 700 : 400 }}>{n.name}</span>
                  <span className="truncate font-bold" style={{ fontSize: 8, letterSpacing: '0.14em', color: MUTED }}>{viaLabel(n)}</span>
                </span>
                {newest && (
                  <span className="relative flex items-center whitespace-nowrap rounded-full bg-black font-bold" style={{ gap: 5, padding: '3px 8px', color: CYAN, fontSize: 8, letterSpacing: '0.14em' }}>
                    <span className="ts-pulse-fast rounded-full" style={{ width: 6, height: 6, background: CYAN }} aria-hidden />NEWEST
                  </span>
                )}
                <span className="relative whitespace-nowrap" style={{ fontSize: 9, letterSpacing: '0.1em', color: MUTED }}>
                  {n.daysIn === 0 ? 'TODAY' : `${n.daysIn} DAY${n.daysIn === 1 ? '' : 'S'}`}
                </span>
              </span>
            );
            return n.handle
              ? <Link key={i} to={`/${n.handle}`} className="block text-black hover:opacity-90">{body}</Link>
              : <React.Fragment key={i}>{body}</React.Fragment>;
          })}
        </div>
      )}
    </div>
  );
}

function VenueRow({ v }) {
  return (
    <div className="flex items-center" style={{ gap: 10, padding: '8px 0', borderTop: '1px solid #EEE' }}>
      <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
        <span className="truncate" style={{ fontSize: 13 }}>{v.name}</span>
        <span className="truncate uppercase" style={{ fontSize: 9, letterSpacing: '0.12em', color: MUTED }}>
          {v.category}{v.distance != null ? ` · ${v.distance} ${v.distanceUnit}` : ''}
        </span>
      </span>
      {v.openNow
        ? <span className="flex-shrink-0 font-bold" style={{ fontSize: 9, letterSpacing: '0.08em', color: '#15803D' }}>{v.statusText || (v.openTil ? `OPEN TIL ${v.openTil}` : 'OPEN')}</span>
        : <span className="flex-shrink-0" style={{ fontSize: 9, letterSpacing: '0.08em', color: '#9CA3AF' }}>HOURS N/A</span>}
    </div>
  );
}

function OpenNowPanel({ town, extrasLoading }) {
  const [sheet, setSheet] = useState(false);
  if (boxPending(extrasLoading, town?.openNow)) return <SkeletonPanel rows={3} />;
  const all = town?.openNow?.data || [];
  const open = all.filter((v) => v.openNow);
  return (
    <div className="flex flex-col bg-white" style={{ padding: 18, gap: 10 }}>
      <PanelHead title="OPEN NOW" src="SRC: PLACES" />
      {open.length === 0
        ? <span className="uppercase" style={{ fontSize: 11, letterSpacing: '0.1em', color: MUTED }}>Nothing with hours open right now.</span>
        : <div>{open.slice(0, 6).map((v, i) => <VenueRow key={i} v={v} />)}</div>}
      {all.length > 0 && (
        <button type="button" onClick={() => setSheet(true)} className="rounded-full font-bold transition-colors hover:bg-black hover:text-white" style={{ height: 36, background: FILL, fontSize: 10, letterSpacing: '0.14em' }}>
          SEE ALL {all.length} PLACES →
        </button>
      )}
      {sheet && <VenueSheet venues={all} onClose={() => setSheet(false)} />}
    </div>
  );
}

function VenueSheet({ venues, onClose }) {
  const groups = [['GATHER', venues.filter((v) => v.rank <= 4)], ['EAT & DRINK', venues.filter((v) => v.rank >= 5)]];
  return (
    <div className="fixed inset-0 safe-inset z-[90] flex items-end justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative max-h-[80vh] w-full max-w-[520px] overflow-y-auto border-2 border-black bg-white" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Open in town">
        <div className="sticky top-0 flex items-center justify-between border-b-2 border-black bg-white px-4 py-3">
          <span className="font-bold uppercase" style={{ fontSize: 11, letterSpacing: '0.14em' }}>Open in town · {venues.length}</span>
          <button type="button" onClick={onClose} className="rounded-full bg-black px-3 py-1 font-bold text-white" style={{ fontSize: 10, letterSpacing: '0.14em' }}>CLOSE</button>
        </div>
        {groups.map(([label, list]) => (list.length === 0 ? null : (
          <div key={label}>
            <div className="bg-black px-4 py-1.5 font-bold uppercase text-white" style={{ fontSize: 9, letterSpacing: '0.14em' }}>{label}</div>
            <div className="px-4">{list.map((v, i) => <VenueRow key={i} v={v} />)}</div>
          </div>
        )))}
      </div>
    </div>
  );
}

function TheLine({ town, extrasLoading }) {
  if (boxPending(extrasLoading, town?.sports)) return <SkeletonPanel rows={1} dark />;
  const s = town?.sports?.data;
  if (!s || (!s.line && !s.team)) return null;
  const note = town?.sports?.isFallback ? town?.sports?.fallbackNote : null;
  return (
    <div className="flex items-center justify-between bg-black text-white" style={{ padding: 18, gap: 12 }}>
      <span className="flex min-w-0 flex-col" style={{ gap: 2 }}>
        <span className="font-bold" style={{ fontSize: 11, letterSpacing: '0.16em', color: CYAN }}>THE LINE</span>
        <span className="truncate font-bold uppercase" style={{ fontSize: 14 }}>{[s.team, s.league].filter(Boolean).join(' · ')}</span>
        {note && <span className="truncate uppercase" style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,.55)' }}>{note}</span>}
      </span>
      <span className="flex-shrink-0 text-right uppercase" style={{ fontSize: 11, letterSpacing: '0.12em', color: 'rgba(255,255,255,.7)' }}>{s.line || 'NO GAME TONIGHT'}</span>
    </div>
  );
}

function LocalsPanel({ town, extrasLoading }) {
  if (boxPending(extrasLoading, town?.locals)) return null;
  const rows = town?.locals?.data || [];
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col bg-white" style={{ padding: 18, gap: 12 }}>
      <PanelHead title={`THE LOCALS · ${rows.length}`} src="SRC: PORTAL" />
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(34px,1fr))', gap: 6 }}>
        {rows.map((l, i) => {
          const tile = (
            <span className="flex items-center justify-center overflow-hidden font-bold" style={{ aspectRatio: '1', background: '#E5E7EB', fontSize: 9 }}>
              {l.avatarUrl ? <img src={l.avatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : l.initials}
            </span>
          );
          return l.handle
            ? <Link key={i} to={`/${l.handle}`} title={l.name} className="block hover:opacity-70">{tile}</Link>
            : <React.Fragment key={i}>{tile}</React.Fragment>;
        })}
      </div>
    </div>
  );
}

function DealsPanel({ town, extrasLoading }) {
  if (boxPending(extrasLoading, town?.deals)) return null;
  const rows = town?.deals?.data || [];
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col bg-white" style={{ padding: 18, gap: 10 }}>
      <PanelHead title="DEALS FOR THE CITY" src="SRC: SPONSORS" />
      {rows.map((d, i) => (
        <div key={i} className="flex items-center" style={{ gap: 10, paddingTop: i ? 10 : 0, borderTop: i ? '1px solid #EEE' : 'none' }}>
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 2 }}>
            <span className="font-bold" style={{ fontSize: 13 }}>{d.brand}</span>
            {d.message && <span style={{ fontSize: 11, color: MUTED }}>{d.message}</span>}
          </span>
          {d.promoLink && (
            <a href={d.promoLink} target="_blank" rel="noreferrer" className="flex-shrink-0 rounded-full bg-black font-bold text-white hover:opacity-80" style={{ padding: '6px 12px', fontSize: 10, letterSpacing: '0.14em' }}>OPEN</a>
          )}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------ THE BOARD ------------------------------ */

const BOARD_CATS = ['RIDE SHARE', 'LOST DOG', 'HELP WANTED', 'FOR SALE', 'OTHER'];
const BOARD_TINT = { 'RIDE SHARE': '#A8D8EA', 'LOST DOG': '#FFCCE6', 'HELP WANTED': '#FAE1B4', 'FOR SALE': '#C6B8FF', OTHER: '#E5E7EB' };

function BoardPanel({ town, slug, onReload, extrasLoading }) {
  const { showConfirm } = useDialog();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [cat, setCat] = useState('RIDE SHARE');
  const [body, setBody] = useState('');
  const [expires, setExpires] = useState('');
  const [busy, setBusy] = useState(false);
  if (boxPending(extrasLoading, town?.bulletin)) return <SkeletonPanel rows={2} />;
  const rows = town?.bulletin?.data || [];

  const reset = () => { setOpen(false); setEditId(null); setBody(''); setExpires(''); setCat('RIDE SHARE'); };
  const startEdit = (b) => {
    setEditId(b.id);
    setCat(b.category || 'OTHER');
    setBody(b.body || '');
    setExpires(b.expiresAt ? new Date(b.expiresAt).toISOString().slice(0, 10) : '');
    setOpen(true);
  };
  const submit = async () => {
    if (!body.trim() || busy) return;
    setBusy(true);
    const { squareService } = await import('../../services');
    const payload = { category: cat, body: body.trim(), expiresAt: expires ? new Date(`${expires}T23:59:59`).toISOString() : null };
    const r = editId ? await squareService.updateBulletin(slug, editId, payload) : await squareService.postBulletin(slug, payload);
    setBusy(false);
    if (r.success) { reset(); if (onReload) onReload(); }
  };
  const remove = async (b) => {
    const ok = await showConfirm({ title: 'DELETE POST', message: 'Remove this from the board?', confirmLabel: 'DELETE' });
    if (!ok) return;
    const { squareService } = await import('../../services');
    const r = await squareService.deleteBulletin(slug, b.id);
    if (r.success && onReload) onReload();
  };

  return (
    <div className="flex flex-col bg-white" style={{ padding: 18, gap: 12 }} data-testid="the-board">
      <span className="font-bold" style={{ fontSize: 11, letterSpacing: '0.16em' }}>THE BOARD</span>
      {rows.length === 0 && !open && <span style={{ fontSize: 12, color: MUTED }}>Nothing on the board yet.</span>}
      {rows.map((b, i) => (
        <div key={b.id || i} className="flex items-start justify-between" style={{ gap: 8 }}>
          <span style={{ fontSize: 12, lineHeight: 1.45 }}>
            <b className="whitespace-nowrap" style={{ fontSize: 9, letterSpacing: '0.14em', padding: '2px 6px', background: BOARD_TINT[b.category] || BOARD_TINT.OTHER }}>{b.category}</b>{' '}
            {b.body}
            {b.expiresAt && <span className="whitespace-nowrap" style={{ fontSize: 9, letterSpacing: '0.1em', color: '#9CA3AF' }}> · TIL {new Date(b.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()}</span>}
          </span>
          {b.isMine && (
            <span className="flex flex-shrink-0" style={{ gap: 8 }}>
              <button type="button" onClick={() => startEdit(b)} className="font-bold hover:underline" style={{ fontSize: 9, letterSpacing: '0.1em', color: MUTED }}>EDIT</button>
              <button type="button" onClick={() => remove(b)} className="font-bold text-red-600 hover:underline" style={{ fontSize: 9, letterSpacing: '0.1em' }}>DELETE</button>
            </span>
          )}
        </div>
      ))}
      {open ? (
        <div className="flex flex-col" style={{ gap: 8 }}>
          <div className="flex flex-wrap" style={{ gap: 6 }}>
            {BOARD_CATS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCat(c)}
                className="rounded-full font-bold"
                style={{ padding: '4px 10px', fontSize: 9, letterSpacing: '0.1em', background: c === cat ? '#000' : BOARD_TINT[c], color: c === cat ? '#fff' : '#000' }}
              >
                {c}
              </button>
            ))}
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 280))}
            rows={2}
            placeholder="Keep it short. 280 characters."
            className="w-full border-2 border-black p-2 font-mono focus:border-black focus:outline-none focus:ring-0"
            style={{ fontSize: 12 }}
          />
          <label className="font-bold uppercase" style={{ fontSize: 8, letterSpacing: '0.1em', color: MUTED }}>
            Expires (optional, defaults to 2 weeks)
            <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="mt-1 block w-full border-2 border-black p-2 font-mono focus:border-black focus:outline-none focus:ring-0" style={{ fontSize: 12 }} />
          </label>
          <div className="flex" style={{ gap: 8 }}>
            <button type="button" onClick={submit} disabled={busy || !body.trim()} className="flex-1 rounded-full bg-black font-bold text-white disabled:opacity-50" style={{ height: 40, fontSize: 11, letterSpacing: '0.14em' }}>
              {editId ? 'SAVE' : 'POST'}
            </button>
            <button type="button" onClick={reset} className="rounded-full font-bold" style={{ height: 40, padding: '0 18px', background: FILL, fontSize: 11, letterSpacing: '0.14em' }}>CANCEL</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)} className="rounded-full font-bold text-black transition-opacity hover:opacity-80" style={{ height: 40, background: CYAN, fontSize: 11, letterSpacing: '0.14em' }}>
          + POST TO THE BOARD
        </button>
      )}
    </div>
  );
}

/* ------------------------------ CLUBS ------------------------------ */

function ClubsTab({ town, onStartClub, navigate }) {
  const clubs = town?.clubs?.data || [];
  return (
    <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: 14 }}>
      <div className="flex flex-col justify-between" style={{ background: CYAN, padding: 20, gap: 16, minHeight: 150, animation: RISE }}>
        <span className="flex flex-col" style={{ gap: 8 }}>
          <span className="font-bold" style={{ fontSize: 11, letterSpacing: '0.2em' }}>DON&apos;T SEE YOURS?</span>
          <span style={{ fontSize: 14, lineHeight: 1.5 }}>Start it. Every club began with one person choosing to lead.</span>
        </span>
        <button type="button" onClick={onStartClub} className="rounded-full bg-black font-bold text-white transition-colors hover:bg-white hover:text-black" style={{ height: 44, fontSize: 12, letterSpacing: '0.14em' }}>
          START A CLUB
        </button>
      </div>
      {clubs.map((c) => <ClubCard key={c.id} club={c} onOpen={() => navigate(`/clubs/${c.id}`)} />)}
    </div>
  );
}

function ClubCard({ club: c, onOpen }) {
  const faces = (c.memberPreview || []).slice(0, 4);
  const members = c.memberCount || 0;
  const more = Math.max(0, members - faces.length);
  const meta = c.nextMeetAt
    ? `${new Date(c.nextMeetAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()}${c.nextMeetVenue ? ` · ${c.nextMeetVenue.toUpperCase()}` : ''}`
    : `${members} MEMBER${members === 1 ? '' : 'S'}`;
  return (
    <button type="button" onClick={onOpen} className="flex flex-col bg-white text-left text-black hover:opacity-95" style={{ animation: RISE }} data-testid="club-card">
      <span className="relative block w-full overflow-hidden" style={{ height: 140, background: c.imageUrl ? '#111' : pastelFor(c.name) }}>
        {c.imageUrl
          ? <img src={c.imageUrl} alt="" loading="lazy" className="block h-full w-full object-cover" />
          : <span className="absolute inset-0 flex items-center justify-center" style={{ fontSize: 34, fontWeight: 800 }}>{initialsOf(c.name).slice(0, 2)}</span>}
        {c.hasLivePortal && (
          <span className="absolute flex items-center rounded-full font-bold text-white" style={{ left: 10, top: 10, gap: 6, padding: '4px 9px', background: LIVE, fontSize: 9, letterSpacing: '0.12em' }}>
            <span className="pr-sq-pulse rounded-full bg-white" style={{ width: 6, height: 6 }} aria-hidden />PORTAL LIVE
          </span>
        )}
      </span>
      <span className="flex w-full flex-col" style={{ padding: '14px 16px 16px', gap: 12 }}>
        <span className="flex min-w-0 flex-col" style={{ gap: 4 }}>
          <span className="truncate" style={{ fontSize: 15, lineHeight: 1.25, fontWeight: 600 }}>{c.name}</span>
          <span className="truncate" style={{ fontSize: 9, letterSpacing: '0.12em', color: MUTED }}>{meta}</span>
        </span>
        <span className="flex items-center justify-between" style={{ gap: 10 }}>
          <span className="flex min-w-0 items-center" style={{ gap: 10 }}>
            {(faces.length > 0 || more > 0) && (
              <span className="flex" style={{ paddingLeft: 8 }}>
                {faces.map((f, j) => (
                  <span key={j} className="box-border flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full font-bold" style={{ width: 28, height: 28, marginLeft: -8, border: '2px solid #fff', background: '#E5E7EB', fontSize: 9 }}>
                    {f.avatarUrl ? <img src={f.avatarUrl} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : f.initials}
                  </span>
                ))}
                {more > 0 && faces.length > 0 && (
                  <span className="box-border flex items-center rounded-full bg-black font-bold text-white" style={{ height: 28, marginLeft: -8, padding: '0 8px', border: '2px solid #fff', fontSize: 9 }}>+{more}</span>
                )}
              </span>
            )}
            <span className="whitespace-nowrap font-bold" style={{ fontSize: 9, letterSpacing: '0.12em' }}>{members} IN</span>
          </span>
          <span className="flex flex-shrink-0 items-center rounded-full font-bold" style={{ height: 32, padding: '0 14px', background: c.isMember ? FILL : '#000', color: c.isMember ? '#000' : '#fff', fontSize: 10, letterSpacing: '0.14em' }}>
            {c.isMember ? 'MEMBER' : 'JOIN'}
          </span>
        </span>
      </span>
    </button>
  );
}

/* ------------------------------ shared ------------------------------ */

function NoData({ label = 'Nothing here right now.' }) {
  return <span className="uppercase" style={{ fontSize: 11, letterSpacing: '0.1em', color: MUTED }}>{label}</span>;
}

function SkeletonPanel({ rows = 3, dark = false }) {
  return (
    <div className="flex flex-col" style={{ padding: 18, gap: 12, background: dark ? '#000' : '#fff' }} aria-hidden>
      <span className="ts-shimmer h-2.5 w-28" style={{ background: dark ? '#2A2A2A' : '#E5E7EB' }} />
      {Array.from({ length: rows }).map((_, i) => (
        <span key={i} className="flex items-center gap-2">
          <span className="ts-shimmer h-9 w-9 flex-shrink-0" style={{ background: dark ? '#2A2A2A' : '#E5E7EB' }} />
          <span className="ts-shimmer h-2.5 flex-1" style={{ maxWidth: `${70 - i * 8}%`, background: dark ? '#1A1A1A' : '#F2F2F2' }} />
        </span>
      ))}
    </div>
  );
}

function SkeletonBody({ twoCol }) {
  return (
    <div className="grid items-start" style={{ gridTemplateColumns: twoCol, gap: 24 }}>
      <div className="flex flex-col" style={{ gap: 24 }}><SkeletonPanel rows={3} dark /><SkeletonPanel rows={4} /><SkeletonPanel rows={3} /></div>
      <div className="flex flex-col" style={{ gap: 16 }}><SkeletonPanel rows={2} /><SkeletonPanel rows={2} /></div>
    </div>
  );
}

function SquareStyles() {
  return (
    <style>{`
      @keyframes prSqSpin { to { transform: rotate(360deg); } }
      @keyframes prSqSpinCCW { to { transform: rotate(-360deg); } }
      @keyframes prSqPulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .35; transform: scale(.7); } }
      @keyframes prSqRipple { from { opacity: .8; transform: scale(1); } to { opacity: 0; transform: scale(1.6); } }
      @keyframes prSqShine { 0% { transform: translateX(-120%); } 60%,100% { transform: translateX(320%); } }
      @keyframes prSqNudge { 0%,100% { transform: translateX(0); } 50% { transform: translateX(3px); } }
      @keyframes prSqRise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
      @keyframes ts-shimmer-kf { 0% { opacity: .55; } 50% { opacity: 1; } 100% { opacity: .55; } }
      @keyframes ts-banner-sweep { 0% { background-position: -150% 0; } 100% { background-position: 250% 0; } }
      @keyframes tsRing { from { background-position: 0 0, 0 0; } to { background-position: 0 0, 200% 0; } }
      .ts-tab-on { background: linear-gradient(#000,#000) padding-box, ${RING} border-box; background-size: 100% 100%, 200% 100%; animation: tsRing 3s linear infinite; }
      .ts-ring-card { background: ${RING}; }
      .ts-spin-6 { animation: prSqSpin 6s linear infinite; }
      .ts-spin-ccw-5 { animation: prSqSpinCCW 5s linear infinite; }
      .pr-sq-pulse { display: inline-block; animation: prSqPulse 1.6s ease-in-out infinite; }
      .ts-pulse-fast { display: inline-block; animation: prSqPulse 1.2s ease-in-out infinite; }
      .ts-ripple { animation: prSqRipple 3.6s cubic-bezier(0,0,.2,1) infinite both; }
      .ts-row-shine { background: linear-gradient(90deg, rgba(0,255,255,0), rgba(0,255,255,.28), rgba(0,255,255,0)); animation: prSqShine 2.4s ease-in-out infinite; }
      .ts-nudge { animation: prSqNudge 1.3s ease-in-out infinite; }
      .ts-lift { transition: transform .2s; }
      .ts-lift:hover { transform: translateY(-1px); }
      .ts-clamp2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      .ts-clamp3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
      .ts-noscrollbar { scrollbar-width: none; }
      .ts-noscrollbar::-webkit-scrollbar { display: none; }
      .ts-dark-input::placeholder { color: rgba(255,255,255,.45); }
      .ts-shimmer { display: inline-block; animation: ts-shimmer-kf 1.2s ease-in-out infinite; }
      .ts-banner-shimmer { background: linear-gradient(100deg, #0b0b0b 0%, #1a1a1a 42%, #2c2c2c 50%, #1a1a1a 58%, #0b0b0b 100%); background-size: 220% 100%; animation: ts-banner-sweep 1.6s linear infinite; }
      @media (prefers-reduced-motion: reduce) {
        .ts-root *, .ts-root *::before, .ts-root *::after { animation: none !important; transition: none !important; }
        .ts-root .ts-ripple, .ts-root .ts-row-shine { display: none; }
      }
    `}</style>
  );
}
